/**
 * Leads Table Component
 *
 * Displays and manages leads data with filtering, pagination, and sorting capabilities.
 * This component handles:
 * - Fetching leads data from the API
 * - Displaying leads in a tabular format
 * - Filtering leads by various criteria
 * - Toggling between complete and partial leads
 * - Pagination of lead results
 * - Displaying system health notifications for lead processing issues
 *
 * The component can function as a standalone page or as a sub-component within
 * another view (like customer details).
 *
 * @module components/leads/leads-table
 */

import { Tab, Tabs } from "react-bootstrap";
import styled from "styled-components";
import { useSearchParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import {
  LEAD_VIEW,
  DATE_FORMAT,
  ACCESS_CONFIG,
  FUNNEL_TYPE_FILTER,
  DATE_PICKER_DISPLAY_FORMAT
} from "../strings";
import {
  CustomCard,
  PageHeading,
  HeaderColumn,
  BootstrapTable,
  LastHeaderColumn,
  FirstHeaderColumn
} from "../common-styling";
import {
  getAllLeads,
  getAllPartialLeads,
  getSystemHealthErrorByApi,
  getDealStages,
  getActiveCCIntegration
} from "../../services/api-declaration";
import {
  generateSystemIssueText,
  generateSystemActionText
} from "../../services/common-service";
import {
  leadFilterConfig,
  upcomingLeadFilterConfig
} from "../funnels-table/filter-config";
import LeadsTableRow from "./leads-table-row";
import TableLoader from "../loader/table-loader";
import ViewMoreButton from "../view-more-button";
import { hasAccess } from "../../services/access";
import { showError } from "../../services/toast-service";
import AdvancedFilters from "../table-filter/advanced-filter";
import CustomPagination from "../pagination/custom-pagination";
import { useTranslation } from "react-i18next";

/**
 * Styled component for system health error notifications
 * Uses orange background to indicate warning status
 */
const ErrorNotificationBanner = styled.div`
  background: orange;
  padding: 2px;
  padding-left: 20px;
  color: white;
  font-size: 12px;
  font-weight: bold;
`;

/**
 * Leads table component for displaying and managing leads
 *
 * @param {Object} props - Component properties
 * @param {string|null} props.customerId - Optional customer ID to filter leads
 * @param {boolean} props.asSubComponent - Whether component is used within another view
 * @param {number} props.limit - Optional limit for number of leads to display
 * @returns {JSX.Element} Leads table component
 */
const LeadsTable = ({ customerId = null, asSubComponent = false, limit }) => {
  const { t } = useTranslation();

  // Component state
  const [leads, setLeads] = useState([]); // Lead data array
  const [isLoading, setIsLoading] = useState(true); // Loading state
  const [paginateOpts, setPaginateOpt] = useState({}); // Pagination options
  const [shouldResetFilters, setShouldResetFilters] = useState(false); // Filter reset flag
  const [systemHealthData, setSystemHealthData] = useState(null); // System health warnings
  const [searchParams, setSearchParams] = useSearchParams(); // URL query parameters
  const [dealStageOptions, setDealStageOptions] = useState([]);
  const [baseDealStageOptions, setBaseDealStageOptions] = useState([]);
  const [isDealStageDisabled, setIsDealStageDisabled] = useState(false);

  // Lead view constants
  const { COMPLETED, PARTIAL } = LEAD_VIEW;
  const isPartialTabSelected = searchParams.get("view") === PARTIAL; // Whether partial leads tab is active

  /**
   * Fetch leads data from the API
   * Handles both complete and partial leads based on tab selection
   */
  const fetchLeads = async () => {
    try {
      setIsLoading(true);

      // Prepare query parameters
      const params = {};
      for (const [key, value] of searchParams.entries()) {
        params[key] = value;
      }

      // Add optional filters
      if (customerId) {
        params.customer = customerId;
      }
      if (limit) {
        params.limit = limit;
      }

      // Fetch either partial or complete leads based on tab
      let response = null;
      if (isPartialTabSelected) {
        // Partial/incomplete leads
        response = await getAllPartialLeads(params);
      } else {
        // Complete leads
        response = await getAllLeads(params);
      }

      // Update state with response data
      const data = response.data.data;
      setLeads(data.rows);

      // Set pagination options
      setPaginateOpt({
        totalPages: data.totalPages,
        hasPrevPage: data.hasPrevPage,
        hasNextPage: data.hasNextPage,
        totalDocs: data.totalDocs,
        rowsOnPage: data.rows.length
      });
    } catch (error) {
      console.log(error);
      // Show error message
      showError(
        error.response.data.message ||
          t("toastMessage.errorInGetting", { what: "leads" })
      );
    } finally {
      setIsLoading(false);
    }
  };
  /**
   * Update URL query parameters
   *
   * @param {string} property - The parameter name
   * @param {string} value - The parameter value
   */
  const handleQueryParams = (property, value) => {
    searchParams.set(property, value);
    setSearchParams(searchParams);
  };

  /**
   * Handle tab selection between complete and partial leads
   * Resets filters and pagination when switching tabs
   *
   * @param {string} evKey - The selected tab key
   */
  const handleSelectTab = (evKey) => {
    searchParams.set("view", evKey);
    searchParams.set("pageIndex", "1");

    // Remove filters that are only available in completed leads view
    // to prevent invalid filter combinations
    searchParams.delete("source");
    searchParams.delete("status");
    searchParams.delete("archive");
    searchParams.delete("dealStage");

    setSearchParams(searchParams);
    setShouldResetFilters(true); // Trigger filter reset in child components
  };

  // Memoized callback for fetching leads to prevent unnecessary re-renders
  const leadsCallback = useCallback(
    fetchLeads,
    // eslint-disable-next-line
    [searchParams, customerId, limit]
  );

  /**
   * Fetch system health data for lead processing errors
   * Only runs once on component mount
   */
  useEffect(() => {
    const fetchLeadError = async () => {
      try {
        // Check for errors in the lead update process specifically
        const sysHealthRes = await getSystemHealthErrorByApi({
          apiName: "UPDATE_LEAD_ON_CC" // Contact Cloud lead update errors
        });
        setSystemHealthData(sysHealthRes.data.data[0]);
      } catch (error) {
        console.log("Error in fetch system health alert", error);
        showError("Error in fetch system health alert!");
      }
    };
    fetchLeadError();
  }, []);

  /**
   * Fetch leads data when search parameters change
   * Uses the memoized callback to prevent unnecessary API calls
   */
  useEffect(() => {
    leadsCallback();
  }, [leadsCallback]);

  const fetchDealStagesForFunnel = async (funnelId) => {
    try {
      if (!funnelId) {
        return { stages: baseDealStageOptions, disabled: false };
      }

      const integration = await getActiveCCIntegration(funnelId);
      const tenantId = integration?.data?.data?.sourceData?.meta?.tenantId;

      if (!tenantId) {
        return { stages: baseDealStageOptions, disabled: true };
      }

      const res = await getDealStages({ tenant: tenantId });
      const tenantStages = res.data?.data || [];
      const merged = mergeDealStages([
        ...baseDealStageOptions,
        ...tenantStages
      ]);

      return { stages: merged, disabled: false };
    } catch (err) {
      console.error("Error fetching funnel stages", err);
      return { stages: baseDealStageOptions, disabled: true };
    }
  };

  useEffect(() => {
    if (isPartialTabSelected) return;

    const loadGlobalStages = async () => {
      try {
        const res = await getDealStages();
        const globalStages = res.data?.data || [];

        setBaseDealStageOptions(globalStages);
        setDealStageOptions(groupDealStages(globalStages));
        setIsDealStageDisabled(false);
      } catch (err) {
        console.error("Error in fetch deal stages", err);
        showError("Error fetching deal stages!");
        setBaseDealStageOptions([]);
        setDealStageOptions([]);
        setIsDealStageDisabled(true);
      }
    };

    loadGlobalStages();
  }, [isPartialTabSelected]);

  useEffect(() => {
    if (isPartialTabSelected || !baseDealStageOptions) return;
    const funnelId = searchParams.get("funnel");
    if (!funnelId) return;
    const syncFromUrl = async () => {
      const { stages, disabled } = await fetchDealStagesForFunnel(funnelId);
      setDealStageOptions(groupDealStages(stages));
      setIsDealStageDisabled(disabled);
    };
    syncFromUrl();
    //  eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPartialTabSelected, baseDealStageOptions]);

  const mergeDealStages = (stages) => {
    const seen = new Set();
    return stages.filter((stage) => {
      if (seen.has(stage._id)) return false;
      seen.add(stage._id);
      return true;
    });
  };

  const groupDealStages = (stages, selectedFunnelType = "") => {
    if (!Array.isArray(stages)) return [];
    const leads = [];
    const employees = [];
    const dynamic = [];

    stages.forEach((stage) => {
      const stageType = stage?.type || "";
      if (stageType === FUNNEL_TYPE_FILTER.LEAD) {
        leads.push(stage);
      } else if (stageType === FUNNEL_TYPE_FILTER.EMPLOYEE) {
        employees.push(stage);
      } else {
        dynamic.push(stage);
      }
    });

    const groups = [];

    const includeLeads =
      !selectedFunnelType || selectedFunnelType === FUNNEL_TYPE_FILTER.LEAD;
    const includeEmployees =
      !selectedFunnelType || selectedFunnelType === FUNNEL_TYPE_FILTER.EMPLOYEE;

    if (includeLeads && leads.length)
      groups.push({ label: `Leads DealStages`, options: leads });
    if (includeEmployees && employees.length)
      groups.push({ label: `Employees DealStages`, options: employees });
    if (dynamic.length)
      groups.push({ label: `Dynamic DealStages`, options: dynamic });
    return groups;
  };

  const onFunnelChange = async (option) => {
    if (isPartialTabSelected) return;

    const funnelId = option?._id || null;
    const { stages, disabled } = await fetchDealStagesForFunnel(funnelId);
    const funnelType = option?.general?.funnelType;
    setDealStageOptions(groupDealStages(stages, funnelType));
    setIsDealStageDisabled(disabled);
  };

  const getDynamicLeadFilterConfig = () =>
    leadFilterConfig.map((f) =>
      f.name === "dealStage"
        ? { ...f, options: dealStageOptions, isDisabled: isDealStageDisabled }
        : f
    );

  return (
    <div>
      <PageHeading className="">Leads</PageHeading>
      <div className="mt-3">
        {!asSubComponent && (
          <div>
            <AdvancedFilters
              components={
                isPartialTabSelected
                  ? upcomingLeadFilterConfig
                  : getDynamicLeadFilterConfig()
              }
              heading="Leads"
              pageName="search"
              placeholder={t("userSearchPlaceholder")}
              btnText="Lead"
              hideAddNew
              downloadLeadsButton={
                !isPartialTabSelected && hasAccess(ACCESS_CONFIG.ADMIN_TIER)
              }
              isDownloadLeadsButtonDisabled={
                !searchParams.get("funnel") || !paginateOpts.totalDocs
              }
              dateInputFormat={DATE_PICKER_DISPLAY_FORMAT}
              dateOutputFormat={DATE_FORMAT}
              resetFiltersForcefully={shouldResetFilters}
              setResetFiltersForcefully={setShouldResetFilters}
              onAsyncFilterSelect={onFunnelChange}
            />
          </div>
        )}
      </div>
      <Tabs
        id="justify-tabs"
        className="mt-4 mb-2 nav-tabs"
        onSelect={(evKey) => handleSelectTab(evKey)}
        activeKey={isPartialTabSelected ? PARTIAL : COMPLETED}
      >
        <Tab eventKey={COMPLETED} title={t("leadTabs.leads")}></Tab>
        <Tab eventKey={PARTIAL} title={t("leadTabs.incompleteLeads")}></Tab>
      </Tabs>
      {systemHealthData && (
        <ErrorNotificationBanner>
          Alert! {generateSystemIssueText(systemHealthData.issue)} error
          occurred in {generateSystemActionText(systemHealthData.action)}.
        </ErrorNotificationBanner>
      )}
      <CustomCard className="mb-3 p-3 rounded-0">
        <BootstrapTable responsive>
          <thead>
            <tr>
              <FirstHeaderColumn>{t("common.name")}</FirstHeaderColumn>
              {!isPartialTabSelected && (
                <HeaderColumn className="text-center">
                  {t("common.syncStatus")}
                </HeaderColumn>
              )}
              <HeaderColumn>Funnel / {t("common.customer")}</HeaderColumn>
              {!isPartialTabSelected && (
                <>
                  <HeaderColumn className="text-center">CC Stage</HeaderColumn>
                  <HeaderColumn className="text-center">Volume</HeaderColumn>
                  <HeaderColumn $minWidth="230" className="text-center">
                    {t("forwardedOnContactCloud")}
                  </HeaderColumn>
                </>
              )}
              <HeaderColumn>
                {t("common.created")} {t("common.on")}
              </HeaderColumn>
              {!isPartialTabSelected && (
                <HeaderColumn>{t("common.telephone")}</HeaderColumn>
              )}
              <HeaderColumn>{t("common.postalCode")}</HeaderColumn>
              <LastHeaderColumn $empty></LastHeaderColumn>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <TableLoader colSpan={9} />
            ) : (
              <LeadsTableRow leads={leads} fetchLeads={fetchLeads} />
            )}
          </tbody>
        </BootstrapTable>
      </CustomCard>
      {!asSubComponent ? (
        <CustomPagination
          paginateOpts={paginateOpts}
          activeTab={Number(searchParams.get("pageIndex")) || 1}
          select={(val) => handleQueryParams("pageIndex", val)}
        />
      ) : paginateOpts.totalDocs > 0 ? (
        <ViewMoreButton
          id={customerId}
          path="../../../leads"
          searchParams={searchParams}
          resultsLength={leads.length}
          totalDocs={paginateOpts.totalDocs}
          handleQueryParams={handleQueryParams}
        />
      ) : (
        <></>
      )}
    </div>
  );
};
export default LeadsTable;
